// ─────────────────────────────────────────────
//  Mobile App section — Setup ?tab=mobile. ADMIN ONLY.
//
//  Lets an administrator choose which modules the installed phone app shows
//  at the selected facility, by writing facility.mobileConfig (top-level key
//  on the facility doc, exactly like sheetAic — admin-only write per the
//  unconditional isAdmin branch of the facility update rule, so no rules
//  change). Save-on-toggle with a toast and live-doc re-seed on failure,
//  copying the AicMarkerSection idiom in setupPage.sheetImport.js.
//
//  CRITICAL — the write always sends the COMPLETE modules map (every registry
//  id, strictly boolean), never a delta. updateFacility is setDoc(merge:true)
//  and Firestore deep-merges nested maps, so a partial write could leave keys
//  this panel no longer shows and drift Setup out of sync with phones
//  (PLATFORM-NOTES R9). Because every write is a full valid config, per-toggle
//  saving never leaves a half-applied configuration either.
//
//  CRITICAL — gated to isAdmin. SetupPage admits admins AND AICs, but the
//  facility update rule field-scopes AIC writes to sheetImportConfig, so an
//  AIC flipping one of these toggles would silently fail with a permission
//  toast. An AIC must not see toggles that cannot work: render nothing.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateFacility } from "../firebase/firestore";
import { useFacility } from "../hooks/useFacility";
import { facilityLabel } from "../domain/facilities";
import {
  MOBILE_CONFIG_VERSION,
  MODULE_REGISTRY,
  resolveMobileConfig,
} from "../domain/mobileConfig";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

const LOCKED_REASON = "Always shown on phones — safety.";

// Display names for the registry's group ids. A group added to the registry
// without an entry here still renders (falls back to the raw id).
const GROUP_LABELS = {
  room: "Room",
  breaks: "Breaks",
  alerts: "Alerts",
  messages: "Messages",
  supervisor: "Supervisor",
  session: "Session",
};

// Ordered unique groups derived from the registry, so a module or group added
// there shows up here with no change to this file.
const GROUPS = MODULE_REGISTRY.reduce(
  (acc, m) => (acc.includes(m.group) ? acc : [...acc, m.group]),
  []
);

// The complete-map write shape (PLATFORM-NOTES §1.3/R9): every registry id
// present, strictly boolean, locked ids forced true regardless of input.
function completeModulesMap(visible) {
  const map = {};
  for (const m of MODULE_REGISTRY) {
    map[m.id] = m.locked === true ? true : visible[m.id] !== false;
  }
  return map;
}

export function MobileAppSection({ facilityId }) {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const { facility, loading } = useFacility(facilityId);
  const [busy, setBusy] = useState(false);

  // The same pure resolver the phone app uses, so Setup and the phone can
  // never disagree about what a given doc means.
  const resolved = useMemo(
    () => resolveMobileConfig(facility, { loaded: !loading }),
    [facility, loading]
  );

  // Live-doc mirror of the switches, re-seeded whenever the doc changes so a
  // failed save never leaves the UI showing an unsaved state (sheetAic idiom).
  const [visible, setVisible] = useState(resolved.modules);
  useEffect(() => { setVisible(resolved.modules); }, [resolved]);

  // Admin gate — see the header comment. Rendering nothing (not "read-only")
  // keeps the diff to SetupPage's existing AIC experience at zero.
  if (!isAdmin) return null;

  const optionalCount = MODULE_REGISTRY.filter((m) => m.locked !== true).length;

  const flip = async (mod) => {
    if (mod.locked === true || busy) return;
    const nextVisible = { ...visible, [mod.id]: !(visible[mod.id] !== false) };
    const modules = completeModulesMap(nextVisible);
    setBusy(true);
    try {
      await updateFacility(facilityId, {
        mobileConfig: {
          v: MOBILE_CONFIG_VERSION,
          modules,
          // Display-only provenance (there is no facility audit trigger) —
          // shown in the status line and the phone's About row.
          updatedBy: user?.uid || "",
          updatedByName: user?.name || "",
          updatedAt: new Date().toISOString(),
        },
      });
      setVisible(modules);
      toast.success(
        modules[mod.id]
          ? `"${mod.label}" will show on phones at ${facilityLabel(facilityId)}.`
          : `"${mod.label}" is now hidden on phones at ${facilityLabel(facilityId)}.`
      );
    } catch (err) {
      console.error("Save mobile module config failed:", err);
      toast.error("Could not save. Administrator rights are required.");
      setVisible(resolved.modules); // re-seed so the switch never lies
    } finally {
      setBusy(false);
    }
  };

  // Status line — derived from the resolver, one line per §1.4 state.
  let statusText = null;
  let statusWarn = false;
  if (loading || resolved.status === "pending") {
    statusText = "Loading facility settings…";
  } else if (resolved.status === "unconfigured") {
    statusText = "Not configured — this facility's app currently shows every module.";
  } else if (resolved.status === "configured") {
    const cfg = facility?.mobileConfig || {};
    const when =
      typeof cfg.updatedAt === "string" && Number.isFinite(Date.parse(cfg.updatedAt))
        ? new Date(cfg.updatedAt).toLocaleDateString()
        : null;
    statusText =
      `${resolved.hidden.length} of ${optionalCount} optional modules hidden.` +
      (cfg.updatedByName ? ` Last changed by ${cfg.updatedByName}${when ? `, ${when}` : ""}.` : "");
  } else {
    // invalid / unsupportedVersion — the repair path: any toggle flip rewrites
    // the whole config cleanly, so say so instead of dead-ending the admin.
    statusWarn = true;
    statusText =
      "These settings are malformed and are being ignored — the app is showing every module. Flip any toggle to rewrite them cleanly.";
  }

  return (
    <div style={s.panel}>
      <div style={s.panelTitle}>📱 Mobile App Modules</div>
      <p style={{ fontSize: 12, color: color.text.secondary, margin: "6px 0 4px", lineHeight: 1.5 }}>
        Choose which modules the installed phone app shows at{" "}
        <strong>{facilityLabel(facilityId)}</strong>. Changes reach every phone at
        this facility the next time it syncs — they do not change this website.
      </p>
      <p style={{ fontSize: 11, color: color.text.muted, margin: "0 0 10px", lineHeight: 1.5 }}>
        This controls what the phone app displays. It is not a permission —
        access is governed by each person's role.
      </p>

      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: statusWarn ? color.accent.error : color.text.secondary,
          padding: "8px 10px",
          borderRadius: 8,
          background: statusWarn ? `${color.accent.error}12` : color.surface.raised,
          border: `1px solid ${statusWarn ? color.accent.error : color.border.default}`,
          marginBottom: 6,
        }}
      >
        {statusText}
      </div>
      {resolved.problems.length > 0 && (
        <ul style={{ fontSize: 11, color: color.accent.error, margin: "0 0 6px", paddingLeft: 18 }}>
          {resolved.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      {GROUPS.map((group) => (
        <div key={group} style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: color.text.muted, textTransform: "uppercase", letterSpacing: ".5px" }}>
            {GROUP_LABELS[group] || group}
          </div>
          {MODULE_REGISTRY.filter((m) => m.group === group).map((m) => {
            const locked = m.locked === true;
            const isOn = locked || visible[m.id] !== false;
            return (
              <div key={m.id} style={s.notifRow}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{m.label}</div>
                  {locked && (
                    <div style={{ fontSize: 11, color: color.text.muted, marginTop: 3 }}>
                      {LOCKED_REASON}
                    </div>
                  )}
                </div>
                <button
                  style={{
                    ...s.toggle,
                    ...(isOn ? s.toggleOn : {}),
                    ...(locked ? { opacity: 0.5, cursor: "not-allowed" } : {}),
                  }}
                  role="switch"
                  aria-checked={isOn}
                  aria-label={m.label}
                  disabled={locked || busy}
                  aria-disabled={locked || undefined}
                  onClick={() => flip(m)}
                >
                  <div style={{ ...s.toggleKnob, ...(isOn ? s.toggleKnobOn : {}) }} />
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
